import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from './use-toast';
// F9.2-X · Frontend Data Governance — KPIs de EVM SOMENTE via SAL.
// Não voltar a importar `emp_motor_flags` ou `emp_vw_evm_*` aqui.
import { getContractKpis, getEvmFlags } from '@/lib/evm/dataAccess';

export interface EmpIndicador {
  id: string;
  contrato_id: string;
  periodo_referencia: string;
  tipo_periodo: string;
  pv: number;
  ev: number;
  ac: number;
  spi: number | null;
  cpi: number | null;
  eac: number | null;
  etc: number | null;
  vac: number | null;
  percentual_fisico_previsto: number;
  percentual_fisico_realizado: number;
  percentual_financeiro_previsto: number;
  percentual_financeiro_realizado: number;
  desvio_prazo_dias: number;
  desvio_custo_percentual: number;
  baseline_bac: number | null;
  congelado: boolean;
  congelado_em: string | null;
  congelado_por: string | null;
  dados_extras: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  /** Source identifier for debugging */
  _evm_source?: 'blindados' | 'epc_qtd';
}

export interface EmpDesvio {
  id: string;
  contrato_id: string;
  indicador_id: string | null;
  boletim_id: string | null;
  tipo: string;
  severidade: string;
  descricao: string;
  valor_referencia: number | null;
  valor_atual: number | null;
  desvio_percentual: number | null;
  impacto_estimado: string | null;
  acao_corretiva: string | null;
  responsavel_nome: string | null;
  responsavel_email: string | null;
  status: string;
  resolvido_por: string | null;
  resolvido_em: string | null;
  resolucao_descricao: string | null;
  dados_extras: Record<string, any> | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface EmpAlerta {
  id: string;
  contrato_id: string;
  regra_id: string | null;
  desvio_id: string | null;
  indicador_id: string | null;
  titulo: string;
  descricao: string | null;
  severidade: string;
  status: string;
  dados_contexto: Record<string, any> | null;
  reconhecido_por: string | null;
  reconhecido_em: string | null;
  resolvido_por: string | null;
  resolvido_em: string | null;
  notas_resolucao: string | null;
  created_at: string;
  updated_at: string;
}

export function useEmpIndicadores(contratoId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // F9.2-X · F1: fonte travada via SAL. Sem flag de motor, sem fallback.
  const flags = getEvmFlags();

  const { data: indicadores = [], isLoading: indicadoresLoading } = useQuery({
    queryKey: ['emp-indicadores', contratoId, flags.source],
    queryFn: async () => {
      const rows = await getContractKpis(contratoId);
      return rows as unknown as EmpIndicador[];
    },
    enabled: !!contratoId,
  });

  const { data: desvios = [], isLoading: desviosLoading } = useQuery({
    queryKey: ['emp-desvios', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_desvios')
        .select('*')
        .eq('contrato_id', contratoId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as EmpDesvio[];
    },
    enabled: !!contratoId,
  });

  const { data: alertas = [], isLoading: alertasLoading } = useQuery({
    queryKey: ['emp-alertas', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_alertas')
        .select('*')
        .eq('contrato_id', contratoId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as EmpAlerta[];
    },
    enabled: !!contratoId,
  });

  // Treat deviation
  const tratarDesvio = useMutation({
    mutationFn: async ({ desvioId, acao, resolucao }: { desvioId: string; acao: string; resolucao?: string }) => {
      const updates: Record<string, any> = {
        acao_corretiva: acao,
        status: resolucao ? 'resolvido' : 'em_tratamento',
        updated_at: new Date().toISOString(),
      };
      if (resolucao) {
        updates.resolucao_descricao = resolucao;
        updates.resolvido_por = user?.id;
        updates.resolvido_em = new Date().toISOString();
      }
      const { error } = await supabase
        .from('emp_desvios')
        .update(updates)
        .eq('id', desvioId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emp-desvios', contratoId] });
      toast({ title: 'Desvio atualizado' });
    },
    onError: (e: any) => toast({ variant: 'destructive', title: 'Erro', description: e.message }),
  });

  // Acknowledge alert
  const reconhecerAlerta = useMutation({
    mutationFn: async (alertaId: string) => {
      const { error } = await supabase
        .from('emp_alertas')
        .update({
          status: 'reconhecido',
          reconhecido_por: user?.id,
          reconhecido_em: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', alertaId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emp-alertas', contratoId] });
      toast({ title: 'Alerta reconhecido' });
    },
    onError: (e: any) => toast({ variant: 'destructive', title: 'Erro', description: e.message }),
  });

  // Resolve alert
  const resolverAlerta = useMutation({
    mutationFn: async ({ alertaId, notas }: { alertaId: string; notas: string }) => {
      const { error } = await supabase
        .from('emp_alertas')
        .update({
          status: 'resolvido',
          resolvido_por: user?.id,
          resolvido_em: new Date().toISOString(),
          notas_resolucao: notas,
          updated_at: new Date().toISOString(),
        })
        .eq('id', alertaId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emp-alertas', contratoId] });
      toast({ title: 'Alerta resolvido' });
    },
    onError: (e: any) => toast({ variant: 'destructive', title: 'Erro', description: e.message }),
  });

  // Freeze indicator (SPI < 0.9 rule)
  const congelarIndicador = useMutation({
    mutationFn: async (indicadorId: string) => {
      const { error } = await supabase
        .from('emp_indicadores')
        .update({
          congelado: true,
          congelado_em: new Date().toISOString(),
          congelado_por: user?.id,
          updated_at: new Date().toISOString(),
        } as any)
        .eq('id', indicadorId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emp-indicadores', contratoId] });
      toast({ title: 'Indicador congelado' });
    },
    onError: (e: any) => toast({ variant: 'destructive', title: 'Erro', description: e.message }),
  });

  // Latest indicator
  const latest = indicadores.length > 0 ? indicadores[indicadores.length - 1] : null;

  return {
    indicadores,
    desvios,
    alertas,
    latest,
    isLoading: indicadoresLoading || desviosLoading || alertasLoading,
    tratarDesvio,
    reconhecerAlerta,
    resolverAlerta,
    congelarIndicador,
    /** F9.2-X · F1 (locked): EPC-QTD permanentemente OFF na UI. */
    isEpcQtdSource: false as const,
  };
}
